import React, { useState, useEffect, useRef } from 'react';
import Card from '../../../Shared/Components/Card/Card';

const BreathingGarden = ({ updateStats }) => {
    const [isBreathing, setIsBreathing] = useState(false);
    const [phase, setPhase] = useState('inhale'); // inhale, hold, exhale, hold
    const [cyclesCompleted, setCyclesCompleted] = useState(0);
    const [plants, setPlants] = useState([]);
    const [sessionTime, setSessionTime] = useState(0);
    const timerRef = useRef(null);

    const breathingPattern = {
        inhale: { duration: 4, next: 'hold1', text: 'Breathe In', color: '#667eea' },
        hold1: { duration: 4, next: 'exhale', text: 'Hold', color: '#764ba2' },
        exhale: { duration: 4, next: 'hold2', text: 'Breathe Out', color: '#11998e' },
        hold2: { duration: 4, next: 'inhale', text: 'Hold', color: '#38ef7d' }
    };

    useEffect(() => {
        if (isBreathing) {
            const duration = breathingPattern[phase].duration * 1000;
            const timer = setTimeout(() => {
                const nextPhase = breathingPattern[phase].next;
                setPhase(nextPhase);
                if (nextPhase === 'inhale') {
                    setCyclesCompleted(prev => prev + 1);
                    addPlant();
                }
            }, duration);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBreathing, phase]);

    useEffect(() => {
        if (isBreathing) {
            timerRef.current = setInterval(() => {
                setSessionTime(prev => prev + 1);
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isBreathing]);

    const addPlant = () => {
        const plantEmojis = ['🌱', '🌿', '🍃', '🌾', '🌻', '🌺', '🌸', '🌼'];
        const newPlant = {
            id: Date.now(),
            emoji: plantEmojis[Math.floor(Math.random() * plantEmojis.length)],
            left: Math.random() * 80 + 10,
            animationDelay: Math.random() * 2
        };
        setPlants(prev => [...prev, newPlant]);
    };

    const startBreathing = () => {
        setIsBreathing(true);
        setPhase('inhale');
    };

    const stopBreathing = () => {
        setIsBreathing(false);
        if (sessionTime > 0) {
            updateStats('breathingMinutes', Math.ceil(sessionTime / 60));
        }
    };

    const resetSession = () => {
        setIsBreathing(false);
        setPhase('inhale');
        setCyclesCompleted(0);
        setPlants([]);
        setSessionTime(0);
        if (timerRef.current) clearInterval(timerRef.current);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getCircleScale = () => {
        if (!isBreathing) return 1;
        if (phase === 'inhale') return 1.5;
        if (phase === 'exhale') return 1;
        return phase === 'hold1' ? 1.5 : 1;
    };

    return (
        <Card>
            <div style={{ padding: '32px' }}>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <h4 style={{ color: '#1f2937', marginBottom: '8px' }}>Breathing Garden</h4>
                    <p style={{ color: '#6b7280', margin: 0 }}>Follow the breathing pattern and watch your garden grow</p>
                </div>

                {/* Stats Row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px',
                    marginBottom: '32px',
                    maxWidth: '600px',
                    margin: '0 auto 32px'
                }}>
                    <div style={{
                        background: '#f3f4f6',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '600', marginBottom: '4px' }}>
                            TIME
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#667eea' }}>
                            {formatTime(sessionTime)}
                        </div>
                    </div>
                    <div style={{
                        background: '#f3f4f6',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '600', marginBottom: '4px' }}>
                            CYCLES
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#11998e' }}>
                            {cyclesCompleted}
                        </div>
                    </div>
                    <div style={{
                        background: '#f3f4f6',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '600', marginBottom: '4px' }}>
                            PLANTS
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#43e97b' }}>
                            {plants.length}
                        </div>
                    </div>
                </div>

                {/* Breathing Circle */}
                <div style={{
                    position: 'relative',
                    width: '300px',
                    height: '300px',
                    margin: '0 auto 32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {/* Animated Circle */}
                    <div style={{
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${breathingPattern[phase].color} 0%, ${breathingPattern[breathingPattern[phase].next].color} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        transform: `scale(${getCircleScale()})`,
                        transition: `transform ${breathingPattern[phase].duration}s ease-in-out`,
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                        zIndex: 2
                    }}>
                        {isBreathing ? breathingPattern[phase].text : 'Ready'}
                    </div>

                    {/* Floating Plants Animation */}
                    {plants.slice(-5).map(plant => (
                        <div
                            key={plant.id}
                            style={{
                                position: 'absolute',
                                fontSize: '2rem',
                                left: `${plant.left}%`,
                                bottom: '0',
                                animation: `float-up 3s ease-out forwards`,
                                animationDelay: `${plant.animationDelay}s`,
                                opacity: 0,
                                zIndex: 1
                            }}
                        >
                            {plant.emoji}
                        </div>
                    ))}
                </div>

                {/* Garden Display */}
                {plants.length > 0 && (
                    <div style={{
                        marginBottom: '32px',
                        padding: '24px',
                        background: 'linear-gradient(to bottom, #f0fdf4 0%, #dcfce7 100%)',
                        borderRadius: '16px',
                        border: '2px solid #86efac',
                        minHeight: '120px'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '16px',
                            color: '#166534',
                            fontSize: '0.9rem',
                            fontWeight: '600'
                        }}>
                            🌱 Your Growing Garden 🌱
                        </div>
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            alignItems: 'flex-end',
                            gap: '8px',
                            minHeight: '60px',
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.5)',
                            borderRadius: '12px'
                        }}>
                            {plants.map((plant, index) => (
                                <div
                                    key={plant.id}
                                    style={{
                                        fontSize: '2rem',
                                        animation: `grow-in 0.5s ease-out forwards`,
                                        animationDelay: `${index * 0.1}s`,
                                        transform: 'scale(0)',
                                        display: 'inline-block',
                                        transition: 'transform 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                >
                                    {plant.emoji}
                                </div>
                            ))}
                        </div>
                        <div style={{
                            textAlign: 'center',
                            marginTop: '12px',
                            color: '#15803d',
                            fontSize: '0.85rem',
                            fontStyle: 'italic'
                        }}>
                            {plants.length} plant{plants.length !== 1 ? 's' : ''} collected
                        </div>
                    </div>
                )}

                <style>{`
                    @keyframes float-up {
                        0% {
                            transform: translateY(0) rotate(0deg);
                            opacity: 1;
                        }
                        100% {
                            transform: translateY(-400px) rotate(360deg);
                            opacity: 0;
                        }
                    }
                    @keyframes grow-in {
                        0% {
                            transform: scale(0) rotate(-180deg);
                            opacity: 0;
                        }
                        50% {
                            transform: scale(1.1) rotate(0deg);
                        }
                        100% {
                            transform: scale(1) rotate(0deg);
                            opacity: 1;
                        }
                    }
                `}</style>

                {/* Controls */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {!isBreathing ? (
                        <button
                            onClick={startBreathing}
                            style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '14px 32px',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                            }}
                        >
                            Start Breathing
                        </button>
                    ) : (
                        <button
                            onClick={stopBreathing}
                            style={{
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '14px 32px',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(240, 147, 251, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(240, 147, 251, 0.3)';
                            }}
                        >
                            Pause
                        </button>
                    )}
                    <button
                        onClick={resetSession}
                        style={{
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '14px 32px',
                            color: '#6b7280',
                            fontWeight: '600',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#e5e7eb';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f3f4f6';
                        }}
                    >
                        Reset
                    </button>
                </div>

                {/* Instructions */}
                <div style={{
                    marginTop: '32px',
                    padding: '20px',
                    background: '#f9fafb',
                    borderRadius: '12px',
                    borderLeft: '4px solid #667eea'
                }}>
                    <h6 style={{ color: '#1f2937', marginBottom: '12px', fontSize: '0.95rem', fontWeight: '600' }}>
                        How to Practice
                    </h6>
                    <ul style={{ color: '#6b7280', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                        <li>Follow the circle as it expands and contracts</li>
                        <li>Breathe in for 4 seconds, hold for 4 seconds</li>
                        <li>Breathe out for 4 seconds, hold for 4 seconds</li>
                        <li>Complete at least 5 cycles for maximum benefit</li>
                        <li>Watch your garden grow with each breath cycle</li>
                    </ul>
                </div>
            </div>
        </Card>
    );
};

export default BreathingGarden;
